<template>
  <div class="time-slots-container">
    <!-- Legend Section -->
    <div class="legend-section">
      <v-chip
        variant="flat"
        class="legend-chip"
        prepend-icon="mdi-calendar-check"
        style="background: white; border: 2px solid #E2E8F0; color: #285096;"
      >
        {{ $t('booking.available') }}
      </v-chip>

      <v-chip
        variant="flat"
        class="legend-chip"
        prepend-icon="mdi-calendar"
        style="background: linear-gradient(90deg, white 50%, rgba(220, 53, 69, 0.2) 50%); border: 2px solid #E2E8F0; color: #285096;"
      >
        {{ $t('booking.partially-booked') }}
      </v-chip>

      <v-chip
        variant="flat"
        class="legend-chip booked"
        prepend-icon="mdi-calendar-remove"
      >
        {{ $t('booking.booked') }}
      </v-chip>
    </div>

    <!-- Timeline Grid -->
    <div class="timeline-wrapper">
      <div class="timeline-grid">
        <div
          v-for="(slot, index) in timeSlots"
          :key="index"
          class="time-slot-wrapper"
        >
          <div class="time-label">
            <v-icon size="14" color="#9AA6B1" class="mr-1">mdi-clock-outline</v-icon>
            <span>{{ timeLabels[index] }}</span>
          </div>

          <v-tooltip location="top">
            <template #activator="{ props }">
              <div
                v-bind="props"
                class="time-slot-bar"
                @mouseenter="hoveredSlotIndex = index"
                @mouseleave="hoveredSlotIndex = null"
              >
                <div
                  v-for="quarter in 4"
                  :key="quarter"
                  class="quarter-slot"
                  :class="{ 'is-filled': slot.filled[quarter - 1] }"
                />
              </div>
            </template>

            <div v-if="slot.bookings.length > 0" class="booking-tooltip">
              <div
                v-for="(booking, idx) in slot.bookings"
                :key="idx"
                class="tooltip-booking"
              >
                <div class="d-flex align-center mb-1">
                  <v-icon size="14" color="white" class="mr-1">mdi-account</v-icon>
                  <span class="tooltip-name">{{ booking.name }}</span>
                </div>
                <div class="d-flex align-center">
                  <v-icon size="14" color="white" class="mr-1">mdi-clock</v-icon>
                  <span class="tooltip-time">{{ booking.start }} - {{ booking.end }}</span>
                </div>
              </div>
            </div>
          </v-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'TimeSlot',
  props: {
    selectedDate: {
      type: Date,
      required: true
    },
    bookings: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      hoveredSlotIndex: null,
      timeLabels: [
        '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
        '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'
      ],
      timeSlots: Array.from({ length: 14 }, () => ({
        filled: [false, false, false, false],
        bookings: [],
      })),
    }
  },
  watch: {
    bookings: {
      handler: 'calculateFilling',
      immediate: true,
    },
  },
  methods: {
    calculateFilling() {
      // Reset time slots
      this.timeSlots = Array.from({ length: 14 }, () => ({
        filled: [false, false, false, false],
        bookings: [],
      }))

      this.bookings.forEach(booking => {
        const startTime = moment.utc(booking.startTime)
        const endTime = moment.utc(booking.endTime)
        this.processRegularBooking(startTime, endTime, booking)
      })
    },

    processRegularBooking(startTime, endTime, booking) {
      this.timeLabels.forEach((label, i) => {
        const slotStart = moment.utc(label, 'HH:mm')
        const slotEnd = moment.utc(slotStart).add(1, 'hour')

        if (startTime.isBefore(slotEnd) && endTime.isAfter(slotStart)) {
          this.fillSlotWithQuarters(slotStart, slotEnd, startTime, endTime, i, booking)
        }
      })
    },

    fillSlotWithQuarters(slotStart, slotEnd, startTime, endTime, index, booking) {
      const quarters = [false, false, false, false]
      const quarterDuration = 15

      for (let q = 0; q < 4; q++) {
        const quarterStart = slotStart.clone().add(q * quarterDuration, 'minutes')
        const quarterEnd = quarterStart.clone().add(quarterDuration, 'minutes')

        if (startTime.isBefore(quarterEnd) && endTime.isAfter(quarterStart)) {
          quarters[q] = true
        }
      }

      this.timeSlots[index].filled = quarters
      this.timeSlots[index].bookings.push({
        start: startTime.format('HH:mm'),
        end: endTime.format('HH:mm'),
        name: booking.creatorName || 'Unknown',
      })
    },
  },
}
</script>

<style scoped>
.time-slots-container {
  width: 100%;
}

/* Legend Section */
.legend-section {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding: 12px;
  background: linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%);
  border-radius: 12px;
  border: 1px solid #E2E8F0;
}

.legend-chip {
  font-weight: 600;
  font-size: 13px;
  height: 36px;
  padding: 0 16px;
}

.legend-chip.booked {
  background: linear-gradient(135deg, #DC3545 0%, #E04747 100%) !important;
  color: white !important;
  border: 2px solid #DC3545;
}

/* Timeline Wrapper */
.timeline-wrapper {
  background: white;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #E2E8F0;
}

.timeline-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 8px;
}

/* Time Slot */
.time-slot-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.time-label {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #4E5965;
  margin-bottom: 8px;
  white-space: nowrap;
}

.time-slot-bar {
  display: flex;
  height: 60px;
  background: white;
  border: 2px solid #E2E8F0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.time-slot-bar:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 80, 150, 0.15);
  border-color: #23A5E6;
}

/* Quarter Slots */
.quarter-slot {
  flex: 1;
  background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%);
  transition: all 0.3s ease;
  position: relative;
}

.quarter-slot:not(:last-child) {
  border-right: 1px solid rgba(226, 232, 240, 0.5);
}

.quarter-slot.is-filled {
  background: linear-gradient(135deg, #DC3545 0%, #E04747 100%);
}

.time-slot-bar:hover .quarter-slot:not(.is-filled) {
  background: linear-gradient(135deg, #EDF3FD 0%, #F3F6FF 100%);
}

/* Tooltip */
.booking-tooltip {
  padding: 8px 12px;
  max-width: 250px;
}

.tooltip-booking {
  padding: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  margin-bottom: 6px;
}

.tooltip-booking:last-child {
  margin-bottom: 0;
}

.tooltip-name {
  font-weight: 700;
  color: white;
  font-size: 13px;
}

.tooltip-time {
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .timeline-grid {
    grid-template-columns: repeat(7, 1fr);
  }
}

@media (max-width: 960px) {
  .timeline-grid {
    grid-template-columns: repeat(5, 1fr);
  }

  .time-slot-bar {
    height: 50px;
  }
}

@media (max-width: 768px) {
  .timeline-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
  }

  .time-label {
    font-size: 10px;
  }

  .time-label .v-icon {
    display: none;
  }

  .time-slot-bar {
    height: 45px;
  }

  .legend-section {
    gap: 6px;
    padding: 8px;
  }

  .legend-chip {
    font-size: 11px;
    height: 32px;
    padding: 0 12px;
  }

  .legend-chip :deep(.v-icon) {
    font-size: 16px;
  }
}

@media (max-width: 600px) {
  .timeline-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .timeline-wrapper {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .timeline-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .legend-section {
    flex-direction: column;
  }

  .legend-chip {
    width: 100%;
    justify-content: center;
  }
}

/* Animation */
.time-slot-wrapper {
  animation: fadeInUp 0.3s ease-out backwards;
}

.time-slot-wrapper:nth-child(1) { animation-delay: 0.05s; }
.time-slot-wrapper:nth-child(2) { animation-delay: 0.1s; }
.time-slot-wrapper:nth-child(3) { animation-delay: 0.15s; }
.time-slot-wrapper:nth-child(4) { animation-delay: 0.2s; }
.time-slot-wrapper:nth-child(5) { animation-delay: 0.25s; }
.time-slot-wrapper:nth-child(6) { animation-delay: 0.3s; }
.time-slot-wrapper:nth-child(7) { animation-delay: 0.35s; }
.time-slot-wrapper:nth-child(8) { animation-delay: 0.4s; }
.time-slot-wrapper:nth-child(9) { animation-delay: 0.45s; }
.time-slot-wrapper:nth-child(10) { animation-delay: 0.5s; }
.time-slot-wrapper:nth-child(11) { animation-delay: 0.55s; }
.time-slot-wrapper:nth-child(12) { animation-delay: 0.6s; }
.time-slot-wrapper:nth-child(13) { animation-delay: 0.65s; }
.time-slot-wrapper:nth-child(14) { animation-delay: 0.7s; }

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>